import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Plus, Trash2, Edit2, Check, X, ChevronsDown, ChevronsRight, LifeBuoy, Archive, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { API } from "@/context";
import { ArtworkCard } from "./ArtworkCard";

const STAGE_COLORS = ["#EAB308", "var(--color-accent)", "#D946EF", "var(--color-primary)", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#14B8A6"];

export function KanbanBoard({ events, selectedEvent, setSelectedEvent, artworks, fetchArtworks, fetchEvents, deleteEvent, updateArtworkStatus, eventProgress }) {
  const [stages, setStages] = useState([]);
  const [draggedArtwork, setDraggedArtwork] = useState(null);
  const [isAddStageOpen, setIsAddStageOpen] = useState(false);
  const [newStageName, setNewStageName] = useState("");
  const [editingStage, setEditingStage] = useState(null);
  const [editStageName, setEditStageName] = useState("");
  const [collapsedStages, setCollapsedStages] = useState({});
  const [editingEvent, setEditingEvent] = useState(null);
  const [editEventName, setEditEventName] = useState("");
  const [draggedEventId, setDraggedEventId] = useState(null);
  const [trashOpen, setTrashOpen] = useState(false);
  const [trashedEvents, setTrashedEvents] = useState([]);

  const fetchTrash = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/events/trash`);
      setTrashedEvents(r.data);
    } catch (e) {
      toast.error("Failed to load trash");
    }
  }, []);

  const openTrash = async () => {
    await fetchTrash();
    setTrashOpen(true);
  };

  const restoreEvent = async (id, name) => {
    try {
      await axios.post(`${API}/events/${id}/restore`);
      toast.success(`Restored "${name}"`);
      await fetchTrash();
      if (fetchEvents) await fetchEvents();
    } catch (e) {
      toast.error("Restore failed: " + (e.response?.data?.detail || e.message));
    }
  };

  const purgeEvent = async (id, name) => {
    if (!window.confirm(`Permanently delete "${name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/events/${id}/purge`);
      toast.success(`Permanently deleted "${name}"`);
      await fetchTrash();
    } catch (e) {
      toast.error("Purge failed: " + (e.response?.data?.detail || e.message));
    }
  };

  const fetchStages = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/stages`);
      setStages(response.data);
    } catch (error) {
      console.error("Error fetching stages:", error);
      toast.error("Failed to load stages");
    }
  }, []);

  useEffect(() => {
    fetchStages();
  }, [fetchStages]);

  const handleDragStart = (e, artwork) => {
    setDraggedArtwork(artwork);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (e, newStatus) => {
    e.preventDefault();
    if (draggedArtwork && draggedArtwork.status !== newStatus) {
      updateArtworkStatus(draggedArtwork.id, newStatus);
    }
    setDraggedArtwork(null);
  };

  const getArtworksByStatus = (status) => {
    return artworks.filter(a => a.status === status);
  };

  const addStage = async () => {
    if (!newStageName.trim()) {
      toast.error("Stage name is required");
      return;
    }

    try {
      await axios.post(`${API}/stages?name=${encodeURIComponent(newStageName)}`);
      toast.success("Stage added");
      setNewStageName("");
      setIsAddStageOpen(false);
      await fetchStages();
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error("Maximum 9 stages allowed");
      } else {
        console.error("Error adding stage:", error);
        toast.error("Failed to add stage");
      }
    }
  };

  const renameStage = async (stageId) => {
    if (!editStageName.trim()) {
      toast.error("Stage name is required");
      return;
    }

    try {
      await axios.put(`${API}/stages/${stageId}?name=${encodeURIComponent(editStageName)}`);
      toast.success("Stage renamed");
      setEditingStage(null);
      setEditStageName("");
      await fetchStages();
      await fetchArtworks(selectedEvent.id);
    } catch (error) {
      console.error("Error renaming stage:", error);
      toast.error("Failed to rename stage");
    }
  };

  const deleteStage = async (stageId) => {
    if (!window.confirm("Are you sure? Artworks in this stage will be moved to 'To Do'")) {
      return;
    }

    try {
      await axios.delete(`${API}/stages/${stageId}`);
      toast.success("Stage deleted");
      await fetchStages();
      await fetchArtworks(selectedEvent.id);
    } catch (error) {
      console.error("Error deleting stage:", error);
      toast.error("Failed to delete stage");
    }
  };

  return (
    <div className="flex gap-8">
      {/* Events Sidebar */}
      <div className="w-80 flex-shrink-0">
        <div className="border border-border bg-white p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-semibold tracking-tight">Events</h2>
          </div>

          <div className="space-y-2" data-testid="events-list">
            {events.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4">No events yet. Create one to get started.</p>
            ) : (
              events.map((event, eventIdx) => {
                const prog = eventProgress[event.id] || { total: 0, started: 0, finished: 0 };
                const remaining = prog.total - prog.started - prog.finished;
                return (
                <div
                  key={event.id}
                  draggable
                  onDragStart={(e) => { setDraggedEventId(event.id); e.dataTransfer.effectAllowed = "move"; }}
                  onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; }}
                  onDrop={async (e) => {
                    e.preventDefault();
                    if (!draggedEventId || draggedEventId === event.id) return;
                    const fromIdx = events.findIndex(ev => ev.id === draggedEventId);
                    const toIdx = eventIdx;
                    const reordered = [...events];
                    const [moved] = reordered.splice(fromIdx, 1);
                    reordered.splice(toIdx, 0, moved);
                    try {
                      await axios.put(`${API}/events/reorder`, reordered.map(ev => ev.id));
                      fetchEvents();
                    } catch (err) { toast.error("Failed to reorder"); }
                    setDraggedEventId(null);
                  }}
                  onDragEnd={() => setDraggedEventId(null)}
                  data-testid={`event-item-${event.id}`}
                  className={`border border-border p-4 cursor-pointer transition-all duration-200 ${
                    draggedEventId === event.id ? "opacity-50" : ""
                  } ${
                    selectedEvent?.id === event.id
                      ? "border-[var(--color-primary)] border-2 bg-[#F4F4F5]"
                      : "hover:border-[var(--color-primary)]"
                  }`}
                  onClick={() => setSelectedEvent(event)}
                >
                  <div className="flex items-center justify-between mb-2">
                    {editingEvent === event.id ? (
                      <div className="flex-1 flex items-center gap-1 mr-1" onClick={(e) => e.stopPropagation()}>
                        <Input
                          value={editEventName}
                          onChange={(e) => setEditEventName(e.target.value)}
                          className="h-7 text-sm rounded-none border-2"
                          autoFocus
                          onKeyDown={async (e) => {
                            if (e.key === 'Enter' && editEventName.trim()) {
                              try {
                                await axios.put(`${API}/events/${event.id}`, { name: editEventName.trim() });
                                toast.success("Event renamed");
                                fetchEvents();
                                setEditingEvent(null);
                              } catch (err) { toast.error("Failed to rename"); }
                            }
                            if (e.key === 'Escape') setEditingEvent(null);
                          }}
                        />
                        <Button size="sm" onClick={async (e) => {
                          e.stopPropagation();
                          if (!editEventName.trim()) return;
                          try {
                            await axios.put(`${API}/events/${event.id}`, { name: editEventName.trim() });
                            toast.success("Event renamed");
                            fetchEvents();
                            setEditingEvent(null);
                          } catch (err) { toast.error("Failed to rename"); }
                        }} className="h-7 px-1.5 bg-[var(--color-primary)] text-white rounded-none"><Check className="h-3 w-3" /></Button>
                        <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); setEditingEvent(null); }} className="h-7 px-1.5 rounded-none"><X className="h-3 w-3" /></Button>
                      </div>
                    ) : (
                      <span className="font-medium text-sm tracking-wide uppercase">{event.name}</span>
                    )}
                    <div className="flex gap-0.5 flex-shrink-0">
                      <Button
                        data-testid={`edit-event-${event.id}`}
                        variant="ghost"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); setEditingEvent(event.id); setEditEventName(event.name); }}
                        className="h-7 w-7 p-0 rounded-none"
                      >
                        <Edit2 className="h-3 w-3" />
                      </Button>
                      <Button
                        data-testid={`delete-event-${event.id}`}
                        variant="ghost"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); deleteEvent(event.id); }}
                        className="h-7 w-7 p-0 hover:bg-destructive hover:text-white rounded-none"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                  {prog.total > 0 && (
                    <div data-testid={`progress-bar-${event.id}`}>
                      <div className="flex h-1.5 w-full overflow-hidden bg-red-200 rounded-sm">
                        {prog.finished > 0 && <div className="bg-green-500 transition-all" style={{ width: `${(prog.finished / prog.total) * 100}%` }} />}
                        {prog.started > 0 && <div className="bg-yellow-400 transition-all" style={{ width: `${(prog.started / prog.total) * 100}%` }} />}
                        {remaining > 0 && <div className="bg-red-400 transition-all" style={{ width: `${(remaining / prog.total) * 100}%` }} />}
                      </div>
                      <div className="flex justify-between mt-1">
                        <span className="text-[10px] text-muted-foreground">{prog.finished}/{prog.total} done</span>
                        <span className="text-[10px] text-muted-foreground">{prog.started} in progress</span>
                      </div>
                    </div>
                  )}
                </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      {/* Kanban Board */}
      <div className="flex-1">
        {selectedEvent ? (
          <div>
            <div className="mb-6 flex items-center justify-between">
              <div>
                <h2 className="text-3xl font-semibold tracking-tight mb-2" data-testid="selected-event-name">{selectedEvent.name}</h2>
                <p className="text-sm font-medium tracking-wide uppercase text-muted-foreground">
                  Artwork Production Status ({stages.length}/9 stages)
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  data-testid="recover-orphans-button"
                  onClick={async () => {
                    if (!window.confirm("Find artworks with invalid stage status and move them back to the first stage?")) return;
                    try {
                      const res = await axios.post(`${API}/stages/recover-orphans`);
                      const count = res.data.recovered || 0;
                      if (count > 0) {
                        toast.success(`Recovered ${count} orphaned artwork${count === 1 ? "" : "s"} to "${res.data.moved_to}"`);
                        if (selectedEvent) await fetchArtworks(selectedEvent.id);
                        if (fetchEvents) await fetchEvents();
                      } else {
                        toast.info("No orphaned artworks found");
                      }
                    } catch (e) {
                      toast.error("Failed to recover orphan artworks");
                    }
                  }}
                  variant="outline"
                  className="rounded-none border-2 px-4 py-2"
                  title="Recover artworks hidden by historical stage renames or deletes"
                >
                  <LifeBuoy className="h-4 w-4 mr-2" />
                  Recover Hidden
                </Button>
                <Button
                  data-testid="trash-button"
                  onClick={openTrash}
                  variant="outline"
                  className="rounded-none border-2 px-4 py-2"
                  title="View and restore deleted events"
                >
                  <Archive className="h-4 w-4 mr-2" />
                  Trash
                </Button>
                <Button
                  onClick={() => setIsAddStageOpen(true)}
                  disabled={stages.length >= 9}
                  className="bg-[var(--color-primary)] text-white hover:bg-[var(--color-accent)] rounded-none px-4 py-2 disabled:opacity-50"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Stage {stages.length >= 9 && "(Max)"}
                </Button>
              </div>
            </div>

            <div className="grid gap-4 overflow-x-auto" style={{ gridTemplateColumns: `repeat(${stages.length}, minmax(210px, 1fr))` }} data-testid="kanban-board">
              {stages.map((stage, index) => {
                const stageArtworks = getArtworksByStatus(stage.name);
                const isStageCollapsed = collapsedStages[stage.id] || false;
                return (
                <div
                  key={stage.id}
                  className="border border-border bg-white p-3"
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, stage.name)}
                  data-testid={`column-${stage.name.toLowerCase().replace(' ', '-')}`}
                >
                  <div className="mb-3">
                    <div className="flex items-center justify-between mb-1.5">
                      {editingStage === stage.id ? (
                        <div className="flex-1 flex items-center gap-1">
                          <Input
                            value={editStageName}
                            onChange={(e) => setEditStageName(e.target.value)}
                            className="rounded-none border-2 text-xs h-7"
                            onKeyPress={(e) => e.key === 'Enter' && renameStage(stage.id)}
                          />
                          <Button size="sm" onClick={() => renameStage(stage.id)} className="h-7 px-1.5 bg-[var(--color-primary)] text-white rounded-none">
                            <Check className="h-3 w-3" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => setEditingStage(null)} className="h-7 px-1.5 rounded-none">
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      ) : (
                        <>
                          <h3 className="text-xs font-medium tracking-wide uppercase truncate">{stage.name}</h3>
                          <div className="flex gap-0.5 flex-shrink-0">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setCollapsedStages(prev => ({ ...prev, [stage.id]: !prev[stage.id] }))}
                              className="h-5 w-5 p-0 rounded-none"
                              data-testid={`collapse-stage-${stage.id}`}
                              title={isStageCollapsed ? "Expand all cards" : "Collapse all cards"}
                            >
                              {isStageCollapsed ? <ChevronsRight className="h-3 w-3" /> : <ChevronsDown className="h-3 w-3" />}
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => { setEditingStage(stage.id); setEditStageName(stage.name); }} className="h-5 w-5 p-0 rounded-none">
                              <Edit2 className="h-2.5 w-2.5" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => deleteStage(stage.id)} className="h-5 w-5 p-0 rounded-none hover:bg-destructive hover:text-white">
                              <Trash2 className="h-2.5 w-2.5" />
                            </Button>
                          </div>
                        </>
                      )}
                    </div>
                    <div className="h-1 w-full" style={{ backgroundColor: STAGE_COLORS[index % STAGE_COLORS.length] }} />
                    <p className="text-[10px] text-muted-foreground mt-1">{stageArtworks.length} items</p>
                  </div>

                  <div className="space-y-2">
                    {stageArtworks.map((artwork) => (
                      <div
                        key={artwork.id}
                        draggable
                        onDragStart={(e) => handleDragStart(e, artwork)}
                        data-testid={`artwork-${artwork.type.toLowerCase().replace(' ', '-')}`}
                      >
                        <ArtworkCard
                          artwork={artwork}
                          onUpdate={() => fetchArtworks(selectedEvent.id)}
                          stageColor={STAGE_COLORS[index % STAGE_COLORS.length]}
                          collapsed={isStageCollapsed}
                        />
                      </div>
                    ))}
                  </div>
                </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center h-[600px] border border-border bg-white" data-testid="no-event-selected">
            <div className="text-center">
              <p className="text-2xl font-semibold mb-2">No Event Selected</p>
              <p className="text-muted-foreground">Select an event or create a new one to view the Kanban board</p>
            </div>
          </div>
        )}
      </div>

      {/* Add Stage Dialog */}
      <Dialog open={isAddStageOpen} onOpenChange={setIsAddStageOpen}>
        <DialogContent className="rounded-none">
          <DialogHeader>
            <DialogTitle className="text-2xl font-semibold">Add New Stage</DialogTitle>
            <DialogDescription>Enter a name for the new workflow stage (max 9 stages)</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <Input
              placeholder="Stage name (e.g., Review, Quality Check)"
              value={newStageName}
              onChange={(e) => setNewStageName(e.target.value)}
              className="rounded-none border-2"
              onKeyPress={(e) => e.key === 'Enter' && addStage()}
            />
            <Button
              onClick={addStage}
              className="w-full bg-[var(--color-primary)] text-white hover:bg-[var(--color-accent)] rounded-none px-6 py-3"
            >
              Add Stage
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={trashOpen} onOpenChange={setTrashOpen}>
        <DialogContent className="max-w-2xl rounded-none border-2">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Archive className="h-5 w-5" />
              Deleted Events (Trash)
            </DialogTitle>
            <DialogDescription>
              Restore an event to bring it back with all its artworks. Purge permanently removes it.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            {trashedEvents.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">Trash is empty</p>
            ) : (
              trashedEvents.map((ev) => (
                <div
                  key={ev.id}
                  data-testid={`trash-event-${ev.id}`}
                  className="border-2 border-border bg-white p-3 flex items-center justify-between gap-3"
                >
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold truncate">{ev.name}</p>
                    <p className="text-xs text-muted-foreground">
                      Deleted: {ev.deleted_at ? new Date(ev.deleted_at).toLocaleString() : "-"}
                      {ev.deleted_by ? ` · by ${ev.deleted_by}` : ""}
                    </p>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <Button
                      data-testid={`restore-${ev.id}`}
                      onClick={() => restoreEvent(ev.id, ev.name)}
                      size="sm"
                      className="bg-[#10B981] text-white hover:bg-[#059669] rounded-none h-8"
                    >
                      <RotateCcw className="h-3 w-3 mr-1" />
                      Restore
                    </Button>
                    <Button
                      data-testid={`purge-${ev.id}`}
                      onClick={() => purgeEvent(ev.id, ev.name)}
                      size="sm"
                      variant="outline"
                      className="rounded-none border-2 text-red-600 hover:bg-red-50 h-8"
                    >
                      <Trash2 className="h-3 w-3 mr-1" />
                      Purge
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
